/* ASK BRILL'S OWN MENU — the sidebar one level down (C1, approved 2026-10-20).
 *
 * Pressing Ask Brill swaps the menu for its chats, with "← Main menu" on top,
 * and the screen behind stays exactly where it is — on a desktop exactly as on
 * a phone. The desktop pop-out beside the main menu (2026-10-20) is gone: her
 * call 2026-10-21 (shape A) — "different than everything else we do". Me is
 * the second level built the same way, with the same `Menu2Header`.
 * It is the first SECOND-LEVEL MENU, and her rule for every one after it: that
 * level takes the `menu2-*` colours (cream blush on the light canvas). The
 * colours arrive from Sidebar as overridden chrome tokens, so this file only
 * ever says `--chrome-*` and stays right in both panel modes.
 *
 * The whole row opens the chat. Rename and delete sit at its end — on hover on
 * a desktop, always on a phone, where there is no hover. Delete asks first
 * (App's ConfirmDialog); rename is typing over the name.
 */
import { useState } from "react";
import { ChevronLeft, MessageSquare, Plus, Pencil, Trash2 } from "lucide-react";
import { groupChats, EARLIER, EARLIER_TITLE, EARLIER_NOTE } from "../utils/askBrillChats";

function ChatRow({ id, title, note, on, isMobile, onOpen, onRename, onDelete, renamable }) {
  const [hover, setHover] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(title);
  const showActs = isMobile || hover || on;

  if (editing) {
    const done = () => { setEditing(false); if (draft.trim() && draft.trim() !== title) onRename(id, draft); };
    return (
      <input
        aria-label="Chat name"
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={done}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();
          if (e.key === "Escape") { setDraft(title); setEditing(false); }
        }}
        style={{
          width: "100%", boxSizing: "border-box", minHeight: 38, padding: "0 10px",
          border: "1.5px solid var(--chrome-accent)", borderRadius: 8,
          background: "var(--menu2-row-on)", color: "var(--chrome-text)",
          fontSize: 13, outline: "none", fontFamily: "inherit",
        }}
      />
    );
  }

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: "flex", alignItems: "center", gap: 2, borderRadius: 8,
        background: on ? "var(--menu2-row-on)" : hover ? "var(--chrome-hover)" : "transparent",
      }}
    >
      <button
        onClick={() => onOpen(id)}
        aria-current={on ? "page" : undefined}
        style={{
          flex: 1, minWidth: 0, minHeight: isMobile ? 44 : 38, border: "none", background: "transparent",
          textAlign: "left", padding: "6px 10px", cursor: "pointer", fontFamily: "inherit",
          display: "flex", flexDirection: "column", gap: 1,
        }}
      >
        <span style={{
          display: "block", width: "100%", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis",
          fontSize: isMobile ? 14 : 13, fontWeight: on ? 600 : 400, color: "var(--chrome-text)",
        }}>{title}</span>
        {note && <span style={{ fontSize: 11, color: "var(--chrome-text-secondary)" }}>{note}</span>}
      </button>
      <span style={{ display: "flex", gap: 2, paddingRight: 4, opacity: showActs ? 1 : 0, transition: "opacity 0.15s" }}>
        {renamable && (
          <button
            onClick={() => { setDraft(title); setEditing(true); }}
            aria-label={`Rename ${title}`} title="Rename"
            style={{ width: 32, height: 32, border: "none", background: "transparent", borderRadius: 8, color: "var(--chrome-text-secondary)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
          >
            <Pencil size={13} />
          </button>
        )}
        <button
          onClick={() => onDelete(id, title)}
          aria-label={`Delete ${title}`} title="Delete"
          style={{ width: 32, height: 32, border: "none", background: "transparent", borderRadius: 8, color: "var(--chrome-text-secondary)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
        >
          <Trash2 size={13} />
        </button>
      </span>
    </div>
  );
}

/* The top of EVERY second-level menu: the way back, then the level's name. */
export function Menu2Header({ onBack, icon: Icon, title }) {
  return (<>
    <button
      onClick={onBack}
      style={{
        alignSelf: "flex-start", display: "flex", alignItems: "center", gap: 6, minHeight: 36, padding: "0 8px",
        border: "none", background: "transparent", borderRadius: 8, color: "var(--chrome-text-secondary)",
        fontSize: 12.5, cursor: "pointer", fontFamily: "inherit",
      }}
    >
      <ChevronLeft size={15} /> Main menu
    </button>
    <p style={{ margin: "4px 10px 8px", display: "flex", alignItems: "center", gap: 8, fontSize: 15, fontWeight: 600, color: "var(--chrome-text)" }}>
      <Icon size={16} color="var(--chrome-accent)" /> {title}
    </p>
  </>);
}

export function AskBrillMenu({ chats = [], hasEarlier = false, activeKey, isMobile, onBack, onNew, onOpen, onRename, onDelete }) {
  const groups = groupChats(chats);
  const label = { margin: "12px 10px 4px", fontSize: 10.5, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--chrome-text-secondary)" };
  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 2, padding: "10px 8px 12px", background: "var(--chrome-bg)" }}>
      <Menu2Header onBack={onBack} icon={MessageSquare} title="Ask Brill" />
      <button
        onClick={onNew}
        style={{
          display: "flex", alignItems: "center", justifyContent: "center", gap: 8, minHeight: 40, margin: "0 2px 6px",
          border: "none", borderRadius: 999, background: "var(--chrome-accent-solid)", color: "var(--chrome-on-accent-solid)",
          fontSize: 13.5, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
        }}
      >
        <Plus size={15} /> New chat
      </button>

      {groups.map((g) => (
        <div key={g.label} style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <p style={label}>{g.label}</p>
          {g.chats.map((c) => (
            <ChatRow key={c.id} id={c.id} title={c.title} on={activeKey === c.id} isMobile={isMobile}
              onOpen={onOpen} onRename={onRename} onDelete={onDelete} renamable />
          ))}
        </div>
      ))}

      {hasEarlier && (
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <p style={label}>Before chats</p>
          <ChatRow id={EARLIER} title={EARLIER_TITLE} note={EARLIER_NOTE} on={activeKey === EARLIER} isMobile={isMobile}
            onOpen={onOpen} onRename={onRename} onDelete={onDelete} renamable={false} />
        </div>
      )}

      {!groups.length && !hasEarlier && (
        <p style={{ margin: "10px 10px", fontSize: 12.5, lineHeight: 1.5, color: "var(--chrome-text-secondary)" }}>
          Your chats will show here, each named from the first thing you ask.
        </p>
      )}
    </div>
  );
}
